import React, { useEffect, useState } from 'react'
import { Calendar, momentLocalizer, Views } from 'react-big-calendar'
import Alert from 'react-bootstrap/Alert'
import Helmet from 'react-helmet'
import { Link, withRouter } from 'react-router-dom'

import Moment from 'moment'

import CalendarAgendaEventComponent from '../components/calendar/CalendarAgendaEventComponent'
import CalendarCustomToolbar from '../components/calendar/CalendarCustomToolbar'
import CalendarEventComponent from '../components/calendar/CalendarEventComponent'
import FAIcon from '../components/FAIcon'
import AppointmentsService from '../services/AppointmentsService'
import CasesService from '../services/CasesService'
import GeneralTasksService from '../services/GeneralTasksService'
import TasksService from '../services/TasksService'

import 'react-big-calendar/lib/sass/styles.scss'
import '../components/common/Skeleton.css'
import './CalendarPage.scss'

const MOBILE_BREAKPOINT = 767
// On mobile, use agenda view. Logic for length is handled by 'length' prop on Calendar
const MOBILE_VIEW_CONFIG = { agenda: true }
// Use object config for allViews to match mobile structure
const ALL_VIEWS_CONFIG = { month: true, week: true, day: true, agenda: true }

const CalendarSkeleton = () => (
  <div
    className="calendar-component shadow my-4 p-4"
    style={{ height: '50rem', backgroundColor: 'white', borderRadius: '0.8rem' }}
  >
    {/* Header Skeleton */}
    <div className="d-flex justify-content-between mb-5 pt-2">
      <div className="d-flex">
        <div className="skeleton mr-2" style={{ width: '80px', height: '38px' }}></div>
        <div className="skeleton mr-2" style={{ width: '80px', height: '38px' }}></div>
        <div className="skeleton" style={{ width: '80px', height: '38px' }}></div>
      </div>
      <div className="skeleton" style={{ width: '200px', height: '38px' }}></div>
      <div className="skeleton" style={{ width: '40px', height: '40px', borderRadius: '50%' }}></div>
    </div>

    {/* Body Skeleton - Generic Grid/List mix */}
    <div className="d-flex flex-column h-75 justify-content-around">
      {[...Array(5)].map((_, i) => (
        <div key={i} className="skeleton" style={{ height: '12%', width: '100%' }}></div>
      ))}
    </div>
  </div>
)

const CalendarPage = withRouter(({ history, user }) => {
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState('')
  const [appointments, setAppointments] = useState([])
  const [tasks, setTasks] = useState([])
  const [generalTasks, setGeneralTasks] = useState([])
  const [cases, setCases] = useState([])
  const [selectedCaseNumber, setSelectedCaseNumber] = useState(() => {
    const params = new URLSearchParams(history?.location?.search || '')
    return params.get('caseNumber') || ''
  })

  const handleCaseFilterChange = nextCaseNumber => {
    setSelectedCaseNumber(nextCaseNumber)
    const params = new URLSearchParams(history?.location?.search || '')
    if (nextCaseNumber) {
      params.set('caseNumber', nextCaseNumber)
    } else {
      params.delete('caseNumber')
    }
    const queryStr = params.toString()
    history.replace({
      pathname: history.location.pathname,
      search: queryStr ? `?${queryStr}` : '',
    })
  }

  // Switch the calendar view to start with Mondays
  Moment.locale(Moment.lang, {
    week: {
      dow: 1,
      doy: 1,
    },
  })

  const localizer = momentLocalizer(Moment)
  // Use object config for allViews to match mobile structure and avoid type-switching issues
  // const allViews = { month: true, week: true, day: true, agenda: true } // This is now ALL_VIEWS_CONFIG

  useEffect(() => {
    setIsLoading(true)
    const promises = []

    // Always fetch appointments (service handles filtering usually, but let's assume same as Home.js)
    // Home.js logic:
    // If Admin/Case Manager -> Fetch All (true flag?)
    // Actually Home.js fetches getAll(true) for Case Manager.
    // Let's implement similar logic.
    // Ideally the API handles "My Cases" vs "All".
    // For now we assume getAll returns what the user is allowed to see.
    // Home.js logic implies admins see everything?
    // "For Case Administrators, show events from all of the tenant's cases."

    // We'll use the same calls as Home.js but distinct: users usually want to see history on a calendar.
    const appointmentsPromise = AppointmentsService.getAll(false).then(r => setAppointments(r))
    promises.push(appointmentsPromise)

    const tasksPromise = TasksService.getAll(false).then(r => setTasks(r))
    promises.push(tasksPromise)

    const generalTasksPromise = GeneralTasksService.getMyTasks().then(r => setGeneralTasks(r))
    promises.push(generalTasksPromise)

    const casesPromise = CasesService.getAll(['caseNumber', 'client.firstName', 'client.lastName']).then(r =>
      setCases(r),
    )
    promises.push(casesPromise)

    Promise.all(promises)
      .then(() => setIsLoading(false))
      .catch(err => {
        setError(err.message || 'An unexpected error occurred')
        setIsLoading(false)
      })
  }, [])

  // Format of event object
  // Event {
  //   id: long,
  //   caseNumber: long
  //   title: string,
  //   start: Date,
  //   end: Date,
  //   isComplete: boolean,
  //   allDay?: boolean
  //   resource?: any,
  // }
  const getEventsList = () => {
    const events = []

    try {
      if (Array.isArray(appointments)) {
        appointments.forEach(a => {
          if (!a.startUtc) return
          if (selectedCaseNumber && String(a.caseNumber) !== String(selectedCaseNumber)) return

          const caseObj = cases.find(c => String(c.caseNumber) === String(a.caseNumber))
          const clientName = caseObj?.client ? `${caseObj.client.firstName} ${caseObj.client.lastName}` : 'Unknown'

          const isAllDay = Boolean(a.isAllDay && a.startUtc)
          const startDate = convertUtcToLocalDateTime(a.startUtc, a)
          if (!startDate) return
          let endDate = a.endUtc ? convertUtcToLocalDateTime(a.endUtc, a) : null
          if (!endDate || endDate < startDate) {
            endDate = isAllDay ? startDate : Moment(startDate).add(1, 'hour').toDate()
          }

          events.push({
            id: a.appointmentNumber,
            caseNumber: a.caseNumber,
            title: `${clientName} - ${a.title}`,
            isComplete: a.complete,
            start: startDate,
            end: endDate,
            allDay: isAllDay,
            type: 'Appointment',
            resource: a,
          })
        })
      }

      if (Array.isArray(tasks)) {
        tasks.forEach(t => {
          if (selectedCaseNumber && String(t.caseNumber) !== String(selectedCaseNumber)) return
          if (t.dueDateUtc) {
            const date = Moment(Moment.utc(t.dueDateUtc).format('YYYY-MM-DD')).toDate()

            const caseObj = cases.find(c => String(c.caseNumber) === String(t.caseNumber))
            const clientName = caseObj?.client ? `${caseObj.client.firstName} ${caseObj.client.lastName}` : 'Unknown'

            events.push({
              id: t.taskNumber,
              caseNumber: t.caseNumber,
              title: `${clientName} - ${t.title}`,
              isComplete: t.completed,
              start: date,
              end: date,
              allDay: true,
              type: 'Task',
              resource: t,
            })
          }
        })
      }

      if (!selectedCaseNumber && Array.isArray(generalTasks)) {
        generalTasks.forEach(t => {
          const dueDate = t.dueDateUtc || t.DueDateUtc
          const title = t.title || t.Title
          const id = t.id || t.Id
          const complete = t.complete || t.Complete

          if (dueDate) {
            const date = Moment(Moment.utc(dueDate).format('YYYY-MM-DD')).toDate()
            events.push({
              id,
              caseNumber: null,
              title,
              isComplete: complete,
              start: date,
              end: date,
              allDay: true,
              type: 'Task',
              resource: t,
            })
          }
        })
      }
    } catch (err) {
      console.error('Error generating calendar events', err)
    }

    return events
  }

  const onSelectEvent = event => {
    const fromPath = history.location.pathname
    if (event.type === 'Appointment') {
      history.push({
        pathname: `/cases/${event.caseNumber}/appointments/${event.id}`,
        state: { from: fromPath },
      })
    } else if (event.type === 'Task') {
      if (event.caseNumber) {
        history.push({
          pathname: `/cases/${event.caseNumber}/tasks/${event.id}`,
          state: { from: fromPath },
        })
      } else {
        history.push({
          pathname: `/tasks/${event.id}`,
          state: { from: fromPath },
        })
      }
    }
  }

  const convertUtcToLocalDateTime = (utcDateTime, appointment) => {
    if (!utcDateTime) return null
    if (appointment?.isAllDay) {
      return Moment(Moment.utc(utcDateTime).format('YYYY-MM-DD'), 'YYYY-MM-DD').startOf('day').toDate()
    }
    return Moment.utc(utcDateTime).local().toDate()
  }

  const formats = {
    timeGutterFormat: 'h A',
  }

  const eventPropGetter = (event, start, end, isSelected) => {
    let className = ''
    const isOverdue = event.type === 'Task' && !event.isComplete && Moment.utc(event.start) < Moment.utc()

    if (event.type === 'Task') {
      if (event.isComplete) className = 'task-event-completed'
      else if (isOverdue) className = 'task-overdue'
      else className = 'task-event'
    } else {
      className = 'appointment-event'
    }

    return { className }
  }

  const eventsList = getEventsList()

  /* Responsive Logic */
  const [isMobile, setIsMobile] = useState(window.innerWidth <= MOBILE_BREAKPOINT)
  const [currentView, setCurrentView] = useState(window.innerWidth <= MOBILE_BREAKPOINT ? Views.AGENDA : Views.MONTH)

  const displayedViews = isMobile ? MOBILE_VIEW_CONFIG : ALL_VIEWS_CONFIG

  useEffect(() => {
    const handleResize = () => {
      const mobile = window.innerWidth <= MOBILE_BREAKPOINT
      if (mobile !== isMobile) {
        setIsMobile(mobile)
      }
    }

    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [isMobile])

  // Effect to switch views when mobile state changes
  useEffect(() => {
    if (isMobile) {
      if (currentView !== Views.AGENDA) {
        setCurrentView(Views.AGENDA)
      }
    } else {
      // If switching back to desktop
      // We force Month view to be safe, ensuring we don't get stuck in a weird state
      if (currentView === Views.AGENDA) {
        setCurrentView(Views.MONTH)
      }
    }
  }, [isMobile, currentView])

  // Ensure we never render an invalid view (e.g. Month view while in Mobile mode which only has Agenda)
  const renderView = isMobile ? Views.AGENDA : currentView

  const sortedCases = [...(cases || [])].sort((a, b) => Number(b.caseNumber || 0) - Number(a.caseNumber || 0))

  return (
    <div className="calendar-page-container">
      <Helmet title="Calendar" />
      {error && (
        <Alert variant="danger">
          <strong>Error:</strong> {error}
        </Alert>
      )}

      {!isLoading && (
        <div
          className="d-flex flex-wrap align-items-center justify-content-between mt-3 mb-1 px-3 py-2 bg-white shadow-sm"
          style={{ borderRadius: '0.5rem' }}
        >
          <div className="d-flex align-items-center flex-wrap my-1">
            <label htmlFor="calendar-case-filter" className="mb-0 mr-2 font-weight-bold text-secondary small">
              <FAIcon name="filter" className="mr-1 text-primary" />
              Filter by Case:
            </label>
            <select
              id="calendar-case-filter"
              className="form-control form-control-sm"
              style={{ width: 'auto', minWidth: '240px' }}
              value={selectedCaseNumber}
              onChange={e => handleCaseFilterChange(e.target.value)}
            >
              <option value="">All Cases ({sortedCases.length})</option>
              {sortedCases.map(c => {
                const cName = c.client ? `${c.client.firstName || ''} ${c.client.lastName || ''}`.trim() : 'Unknown'
                return (
                  <option key={c.caseNumber} value={String(c.caseNumber)}>
                    #{c.caseNumber} — {cName}
                  </option>
                )
              })}
            </select>
            {selectedCaseNumber && (
              <button
                type="button"
                className="btn btn-link btn-sm text-secondary ml-2 p-0"
                onClick={() => handleCaseFilterChange('')}
              >
                <FAIcon name="times-circle" className="mr-1" />
                Clear Filter
              </button>
            )}
          </div>
          {selectedCaseNumber && (
            <div className="my-1">
              <Link to={`/cases/${selectedCaseNumber}`} className="btn btn-outline-primary btn-sm">
                <FAIcon name="folder-open" className="mr-1" />
                Open Case #{selectedCaseNumber}
              </Link>
            </div>
          )}
        </div>
      )}

      {isLoading ? (
        <CalendarSkeleton />
      ) : (
        <Calendar
          key={isMobile ? 'mobile' : 'desktop'} // Force complete re-mount when switching modes to prevent blank screen
          className="calendar-component shadow my-4"
          localizer={localizer}
          formats={formats}
          events={eventsList}
          views={displayedViews}
          view={renderView}
          onView={setCurrentView}
          defaultDate={Moment().startOf('day').toDate()}
          length={90} // Set Agenda view length to 90 days as requested (universally applied if Agenda is active)
          startAccessor="start"
          endAccessor="end"
          onSelectEvent={onSelectEvent}
          eventPropGetter={eventPropGetter}
          components={{
            toolbar: CalendarCustomToolbar,
            month: {
              event: CalendarEventComponent,
            },
            week: {
              event: CalendarAgendaEventComponent,
            },
            day: {
              event: CalendarAgendaEventComponent,
            },
            agenda: {
              event: CalendarAgendaEventComponent,
            },
          }}
        />
      )}
    </div>
  )
})

export default CalendarPage
